import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import Placeholder from '../../../ui/atoms/Placeholder';
import DashboardWidgetShell from './DashboardWidgetShell';
import { CHART_COLORS, widgetFooterLinkClass } from './widgetAnalyticsUi';

const ActivityChartWidget = ({ chartData = [], statsOverviewHref, onNavigate }) => {
  const { t } = useTranslation();

  return (
    <DashboardWidgetShell
      variant="chart"
      title={t('dashboard.agent.activityChart')}
      subtitle={t('dashboard.currentWeek')}
      footer={
        <button
          type="button"
          onClick={() => onNavigate(statsOverviewHref)}
          className={widgetFooterLinkClass}
        >
          {t('dashboard.agent.viewDetailedReports')}
          <ArrowRightIcon className="ml-1 h-3.5 w-3.5" />
        </button>
      }
    >
      {chartData.length > 0 ? (
        <>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="dashGradSessions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.blue} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={CHART_COLORS.blue} stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="dashGradMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS.emerald} stopOpacity={0.15} />
                    <stop offset="100%" stopColor={CHART_COLORS.emerald} stopOpacity={0.01} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#f3f4f6" />
                <XAxis
                  dataKey="displayDate"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const sessionsPoint = payload.find((point) => point.dataKey === 'sessions');
                    const messagesPoint = payload.find((point) => point.dataKey === 'messages');

                    return (
                      <div className="rounded-lg border border-gray-100 bg-white p-3 text-xs shadow-lg">
                        <p className="mb-1 font-semibold text-gray-900">{label}</p>
                        <p className="font-medium text-blue-600">
                          {t('dashboard.sessions')}: {sessionsPoint?.value ?? 0}
                        </p>
                        <p className="font-medium text-emerald-600">
                          {t('dashboard.messages')}: {messagesPoint?.value ?? 0}
                        </p>
                      </div>
                    );
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="sessions"
                  name={t('dashboard.sessions')}
                  stroke={CHART_COLORS.blue}
                  strokeWidth={2}
                  fill="url(#dashGradSessions)"
                  dot={false}
                  activeDot={{ r: 4, fill: CHART_COLORS.blue, stroke: '#fff', strokeWidth: 2 }}
                />
                <Area
                  type="monotone"
                  dataKey="messages"
                  name={t('dashboard.messages')}
                  stroke={CHART_COLORS.emerald}
                  strokeWidth={2}
                  fill="url(#dashGradMessages)"
                  dot={false}
                  activeDot={{ r: 4, fill: CHART_COLORS.emerald, stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex items-center gap-5 pl-1">
            <div className="flex items-center gap-1.5">
              <div className="h-[3px] w-3 rounded-full" style={{ backgroundColor: CHART_COLORS.blue }} />
              <span className="text-xs text-gray-500">{t('dashboard.sessions')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-[3px] w-3 rounded-full" style={{ backgroundColor: CHART_COLORS.emerald }} />
              <span className="text-xs text-gray-500">{t('dashboard.messages')}</span>
            </div>
          </div>
        </>
      ) : (
        <Placeholder
          empty
          size="l"
          message={t('dashboard.noChartData')}
          description={t('dashboard.noChartDataDesc')}
          className="h-72"
        />
      )}
    </DashboardWidgetShell>
  );
};

export default ActivityChartWidget;
